/**
 * Reload/live-op blocker sources for THIS process's reload gate (`RuntimeBuilder`, see
 * runtime-builder.ts): cross-process peer/coordinator sessions (file-backed, below) AND the
 * in-process quiesce registry (`registerInFlightWork`/`getInFlightWorkUnits`) that tracks background
 * lanes, context-scout runs, and isolated completions running in THIS process. The gate reads both;
 * `getPendingReloadBlockers` (cross-process) also backs the `/autonomy` status text.
 */
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import { getAgentDir } from "../config.ts";
import { getProcessWorkRun } from "../utils/work-directory.ts";
import { isProcessAlive } from "./process-liveness.ts";
import { isRecordObject } from "./util/value-guards.ts";

export const ACTIVE_TURN_TTL_MS = 5 * 60_000;
export const AUTO_RELOAD_COORDINATOR_TTL_MS = 10 * 60_000;

export function getReloadCoordinationDir(agentDir: string = getAgentDir()): string {
	return getProcessWorkRun(agentDir, "coordination", "reload", "state").path;
}

export interface ReloadSessionRecord {
	key: string;
	source: "active-turn" | "coordinator";
	pid?: number;
	sessionId?: string;
	sessionFile?: string;
	cwd?: string;
	active?: boolean;
	updatedAt?: number;
	seenAt?: number;
	reloadedAt?: number;
	reason?: string;
}

export interface PendingReloadBlockers {
	pending: boolean;
	reason: string;
	blockers: ReloadSessionRecord[];
	descriptions: string[];
}

export interface ReloadBlockerOptions {
	agentDir?: string;
	now?: number;
	ownKey?: string;
	ownPid?: number;
	ownSessionId?: string;
	ownSessionFile?: string;
	activeTurnTtlMs?: number;
	coordinatorTtlMs?: number;
	/** Auto Learn workers are memory-first, short-lived, and should not block foreground reload/adaptive work by default. */
	includeAutoLearnSessions?: boolean;
	isProcessAlive?: (pid: number | undefined) => boolean;
}

interface ParsedSession {
	pid?: number;
	sessionId?: string;
	sessionFile?: string;
	cwd?: string;
	active?: boolean;
	updatedAt?: number;
	seenAt?: number;
	reloadedAt?: number;
}

function readJsonFile(file: string): unknown {
	try {
		if (!existsSync(file)) return undefined;
		return JSON.parse(readFileSync(file, "utf8")) as unknown;
	} catch {
		return undefined;
	}
}

function stringValue(value: unknown): string | undefined {
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	return undefined;
}

function booleanValue(value: unknown): boolean | undefined {
	return typeof value === "boolean" ? value : undefined;
}

function parseSession(value: unknown): ParsedSession | undefined {
	if (!isRecordObject(value)) return undefined;
	return {
		pid: numberValue(value.pid),
		sessionId: stringValue(value.sessionId),
		sessionFile: stringValue(value.sessionFile),
		cwd: stringValue(value.cwd),
		active: booleanValue(value.active),
		updatedAt: numberValue(value.updatedAt),
		seenAt: numberValue(value.seenAt),
		reloadedAt: numberValue(value.reloadedAt),
	};
}

export { isProcessAlive as isReloadSessionProcessAlive };

function isOwnSession(key: string, session: ParsedSession, options: ReloadBlockerOptions): boolean {
	if (options.ownKey && key === options.ownKey) return true;
	if (options.ownPid !== undefined && session.pid === options.ownPid) return true;
	if (options.ownSessionId && session.sessionId === options.ownSessionId) return true;
	if (options.ownSessionFile && session.sessionFile === options.ownSessionFile) return true;
	return false;
}

function isAutoLearnSession(session: ParsedSession): boolean {
	return !!session.sessionId?.startsWith("auto-learn-");
}

function shouldIgnoreSession(session: ParsedSession, options: ReloadBlockerOptions): boolean {
	return options.includeAutoLearnSessions !== true && isAutoLearnSession(session);
}

function addBlocker(blockers: Map<string, ReloadSessionRecord>, record: ReloadSessionRecord): void {
	const identity = [
		record.key,
		record.pid ?? "",
		record.sessionId ?? "",
		record.sessionFile ?? "",
		record.cwd ?? "",
	].join("\0");
	const existing = blockers.get(identity);
	if (!existing || (existing.source === "coordinator" && record.source === "active-turn")) {
		blockers.set(identity, record);
	}
}

function activeTurnBlockers(options: ReloadBlockerOptions): ReloadSessionRecord[] {
	const agentDir = options.agentDir ?? getAgentDir();
	const now = options.now ?? Date.now();
	const ttl = options.activeTurnTtlMs ?? ACTIVE_TURN_TTL_MS;
	const isAlive = options.isProcessAlive ?? isProcessAlive;
	const registry = readJsonFile(join(getReloadCoordinationDir(agentDir), "active-turns.json"));
	if (!isRecordObject(registry) || !isRecordObject(registry.sessions)) return [];

	const blockers: ReloadSessionRecord[] = [];
	for (const [key, rawSession] of Object.entries(registry.sessions)) {
		const session = parseSession(rawSession);
		if (!session?.active) continue;
		if (isOwnSession(key, session, options) || shouldIgnoreSession(session, options)) continue;
		if (session.updatedAt === undefined || now - session.updatedAt > ttl) continue;
		if (!isAlive(session.pid)) continue;
		blockers.push({ key, source: "active-turn", ...session });
	}
	return blockers;
}

function coordinatorBlockers(options: ReloadBlockerOptions): { blockers: ReloadSessionRecord[]; reason: string } {
	const agentDir = options.agentDir ?? getAgentDir();
	const now = options.now ?? Date.now();
	const ttl = options.coordinatorTtlMs ?? AUTO_RELOAD_COORDINATOR_TTL_MS;
	const isAlive = options.isProcessAlive ?? isProcessAlive;
	const coordinator = readJsonFile(join(getReloadCoordinationDir(agentDir), "auto-reload-state.json"));
	if (!isRecordObject(coordinator) || !isRecordObject(coordinator.changes)) return { blockers: [], reason: "" };

	const blockers: ReloadSessionRecord[] = [];
	let reason = "";
	for (const rawChange of Object.values(coordinator.changes)) {
		if (!isRecordObject(rawChange)) continue;
		const firstSeenAt = numberValue(rawChange.firstSeenAt);
		if (firstSeenAt === undefined || now - firstSeenAt > ttl) continue;
		reason ||= stringValue(rawChange.reason) ?? "";
		if (!isRecordObject(rawChange.sessions)) continue;
		for (const [key, rawSession] of Object.entries(rawChange.sessions)) {
			const session = parseSession(rawSession);
			if (!session) continue;
			if (session.reloadedAt !== undefined) continue;
			if (isOwnSession(key, session, options) || shouldIgnoreSession(session, options)) continue;
			if (!isAlive(session.pid)) continue;
			blockers.push({ key, source: "coordinator", reason: stringValue(rawChange.reason), ...session });
		}
	}
	return { blockers, reason };
}

export function describeReloadSession(
	record: Pick<ReloadSessionRecord, "key" | "pid" | "sessionId" | "sessionFile" | "cwd">,
): string {
	const label = record.sessionId ?? record.sessionFile ?? record.cwd ?? String(record.pid ?? "unknown");
	const parts = [`${record.key}:${label}`];
	if (record.pid !== undefined) parts.push(`pid=${record.pid}`);
	if (record.cwd) parts.push(`cwd=${record.cwd}`);
	if (record.sessionFile) parts.push(`file=${record.sessionFile}`);
	return parts.join(" ");
}

export function getPendingReloadBlockers(options: ReloadBlockerOptions = {}): PendingReloadBlockers {
	const byIdentity = new Map<string, ReloadSessionRecord>();
	for (const blocker of activeTurnBlockers(options)) addBlocker(byIdentity, blocker);
	const coordinator = coordinatorBlockers(options);
	for (const blocker of coordinator.blockers) addBlocker(byIdentity, blocker);
	const blockers = [...byIdentity.values()].sort((a, b) =>
		describeReloadSession(a).localeCompare(describeReloadSession(b)),
	);
	return {
		pending: blockers.length > 0,
		reason:
			coordinator.reason ||
			(blockers.length ? "Pi auto-reload is waiting for active peer/background session(s)." : ""),
		blockers,
		descriptions: blockers.map(describeReloadSession),
	};
}

/**
 * In-process quiesce registry: the reload gate's unified source of truth for background work
 * running IN THIS PROCESS that must finish before a `/reload`, profile switch, or live extension
 * load/unload/reconcile may proceed — background lanes (research/worker/model-fitness), the
 * context-scout's own Agent, and isolated completions (through their single choke point,
 * `ReflectionController.runIsolatedCompletion`) all register here. Foreground streaming/compaction
 * are NOT registered here — the host already exposes those via `RuntimeBuilderDeps.isStreaming`/`isCompacting`,
 * and the gate checks both alongside this registry instead of duplicating that state into a second tracker.
 *
 * Keyed by `agentDir`, matching every cross-process function above, so two sessions rooted at
 * different agent dirs (as every test harness in this repo constructs them) never see each other's
 * in-flight work.
 *
 * Registration is a plain in-memory counter, not file-backed — nothing here survives a process
 * restart, and nothing needs to: a crashed process cannot hold its own reload gate hostage. The
 * returned deregister function MUST be called from a `finally` block by every caller — an
 * un-deregistered unit would wedge the gate forever — so deregistering twice is a safe no-op rather
 * than a footgun for a caller that also deregisters on an early-return path.
 */
export type InFlightWorkKind = "lane" | "scout" | "isolated-completion";

export interface InFlightWorkUnit {
	id: number;
	kind: InFlightWorkKind;
	label: string;
	registeredAt: number;
}

let _nextInFlightWorkId = 1;
const _inFlightWorkByAgentDir = new Map<string, Map<number, InFlightWorkUnit>>();

/** Registers one in-flight background work unit for `agentDir`. Returns its deregister function. */
export function registerInFlightWork(agentDir: string, kind: InFlightWorkKind, label: string): () => void {
	let units = _inFlightWorkByAgentDir.get(agentDir);
	if (!units) {
		units = new Map<number, InFlightWorkUnit>();
		_inFlightWorkByAgentDir.set(agentDir, units);
	}
	const scopedUnits = units;
	const id = _nextInFlightWorkId++;
	scopedUnits.set(id, { id, kind, label, registeredAt: Date.now() });
	let deregistered = false;
	return () => {
		if (deregistered) return;
		deregistered = true;
		scopedUnits.delete(id);
		if (scopedUnits.size === 0) _inFlightWorkByAgentDir.delete(agentDir);
	};
}

/** Live in-flight units for `agentDir`, oldest first. Empty when nothing is registered. */
export function getInFlightWorkUnits(agentDir: string): InFlightWorkUnit[] {
	const units = _inFlightWorkByAgentDir.get(agentDir);
	return units ? [...units.values()].sort((a, b) => a.id - b.id) : [];
}

export function describeInFlightWorkUnit(unit: InFlightWorkUnit): string {
	return `${unit.kind}:${unit.label}`;
}

/**
 * Test-only escape hatch: clears every agentDir's registry so one test's leaked registration cannot
 * block another's reload gate or status text.
 */
export function resetInFlightWorkRegistryForTests(): void {
	_inFlightWorkByAgentDir.clear();
}
